import PAButton, { BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import PropTypes from "prop-types";
import elementType from "prop-types-extra/lib/elementType";
import { Component } from "react";
import PaginationButton from "./PaginationButton";
import styles from "./pagination.module.scss";
import { bsClass, getClassSet, splitBsProps } from "./utils/bootstrapUtils";

const propTypes = {
  activePage: PropTypes.number,
  items: PropTypes.number,
  maxButtons: PropTypes.number,

  /**
   * When `true`, will display the first and the last button page when
   * displaying ellipsis.
   */
  boundaryLinks: PropTypes.bool,

  /**
   * When `true`, will display the default node value ('&hellip;').
   * Otherwise, will display provided node (when specified).
   */
  ellipsis: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.node,
  ]),

  /**
   * When `true`, will display the default node value ('&laquo;').
   * Otherwise, will display provided node (when specified).
   */
  first: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.node,
  ]),

  /**
   * When `true`, will display the default node value ('&raquo;').
   * Otherwise, will display provided node (when specified).
   */
  last: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.node,
  ]),

  /**
   * When `true`, will display the default node value ('&lsaquo;').
   * Otherwise, will display provided node (when specified).
   */
  prev: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.node,
  ]),

  /**
   * When `true`, will display the default node value ('&rsaquo;').
   * Otherwise, will display provided node (when specified).
   */
  next: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.node,
  ]),

  onSelect: PropTypes.func,

  /**
   * You can use a custom element for the buttons
   */
  buttonComponentClass: elementType,
};

const defaultProps = {
  activePage: 1,
  items: 1,
  maxButtons: 0,
  first: false,
  last: false,
  prev: false,
  next: false,
  ellipsis: true,
  boundaryLinks: false,
};

class Pagination extends Component {
  renderPageButtons(
    activePage: any, items: any, maxButtons: any, boundaryLinks: any, ellipsis: any, buttonProps: any,
  ) {
    const { pagePath, onSelect } = this.props;
    const pageButtons = [];

    let startPage;
    let endPage;

    if (maxButtons && maxButtons < items) {
      startPage = Math.max(
        Math.min(

          activePage - Math.floor(maxButtons / 2, 10),
          items - maxButtons + 1,
        ),
        1,
      );
      endPage = startPage + maxButtons - 1;
    } else {
      startPage = 1;
      endPage = items;
    }

    // always show first page, with ellipsis if there's a gap
    if (ellipsis && startPage > 1) {
      pageButtons.push(
        <span
          className={`${styles.circle} ${activePage === 1 ? styles.active : ""}`}
          {...buttonProps}
          key={1}
          eventKey={1}
          active={activePage === 1}
          onClick={(event) => onSelect(1, event)}
        >
          1
        </span>,
      );

      if (startPage > 2) {
        pageButtons.push(
          <span key={`ellipsis-start-${startPage}`} className={`${styles.circle} ${styles.ellipsis}`}>
            ...
          </span>,
        );
      }
    }

    for (let page = startPage; page <= endPage; ++page) {
      pageButtons.push(

        <span
          className={`${styles.circle} ${page === activePage ? styles.active : ""}`}
          {...buttonProps}
          key={page}
          eventKey={page}
          active={page === activePage}
          onClick={(event) => onSelect(page, event)}
        >
          {page}
        </span>,
      );
    }

    // if (ellipsis && boundaryLinks && startPage > 1) {
    //   if (startPage > 2) {
    //     pageButtons.unshift(

    //       <PaginationButton
    //         key="ellipsisFirst"
    //         disabled
    //         componentClass={buttonProps.componentClass}
    //       >

    //         <span aria-label="More">
    //           {ellipsis === true ? "\u2026" : ellipsis}
    //         </span>
    //       </PaginationButton>,
    //     );
    //   }

    //   pageButtons.unshift(

    //     <PaginationButton
    //       {...buttonProps}
    //       key={1}
    //       eventKey={1}
    //       active={false}
    //     >
    //       1
    //     </PaginationButton>,
    //   );
    // }

    if (ellipsis && endPage < items) {
      if (!boundaryLinks || endPage < items - 1) {
        pageButtons.push(
          <span key={`ellipsis-${startPage}-${endPage}`} className={`${styles.circle} ${styles.ellipsis}`}>
            ...
          </span>,
        );
      }

      if (boundaryLinks) {
        pageButtons.push(

          <PaginationButton
            {...buttonProps}
            key={items}
            eventKey={items}
            active={false}
          >
            {items}
          </PaginationButton>,
        );
      }

      const href = pagePath ? `/${pagePath}?pageNumber=${items}` : "#";

      pageButtons.push(

        <span
          className={`${styles.circle} ${items === activePage ? styles.active : ""}`}
          {...buttonProps}
          key={items}
          eventKey={items}
          active={items === activePage}
          onClick={(event) => onSelect(items, event)}
        >
          {items}
        </span>,
      );
    }
    return (
      <div className={styles.pagination_buttons}>
        {pageButtons}
      </div>
    );
  }

  render() {
    const {
      activePage,
      items,
      maxButtons,
      boundaryLinks,
      ellipsis,
      first,
      last,
      prev,
      next,
      onSelect,
      buttonComponentClass,
      className,
      ...props
    } = this.props;

    const [bsProps, elementProps] = splitBsProps(props);

    const classes = getClassSet(bsProps);

    const buttonProps = {
      onSelect,
      componentClass: buttonComponentClass,
    };

    return (
      <div
        {...elementProps}
        className={styles.container}
      >
        {first && (

          <PaginationButton
            {...buttonProps}
            eventKey={1}
            disabled={activePage === 1}
          >
            <span aria-label="First">
              {first === true ? "ddd" : first}
            </span>
          </PaginationButton>
        )}
        {prev && (

          <PAButton
            onClick={(event) => onSelect(activePage - 1, event)}
            buttonType={BUTTON_TYPE.OUTLINE}
            eventKey={activePage - 1}
            disabled={activePage === 1}
            className={styles.navigation_button}
          >
            <ChevronLeft className={styles.left_arrow} strokeWidth={4} size={20} />
            <span className={styles.navigation_button_text}>
              Previous
            </span>
          </PAButton>
        )}

        {this.renderPageButtons(
          activePage, items, maxButtons, boundaryLinks, ellipsis, buttonProps,
        )}

        {next && (

          <PAButton
            onClick={(event) => onSelect(activePage + 1, event)}
            buttonType={BUTTON_TYPE.OUTLINE}
            eventKey={activePage + 1}
            disabled={activePage >= items}
            className={styles.navigation_button}
          >
            <span className={styles.navigation_button_text}>
              Next
            </span>
            <ChevronRight className={styles.right_arrow} strokeWidth={4} size={20} />
          </PAButton>
        )}
        {last && (
          <PaginationButton
            {...buttonProps}
            eventKey={items}
            disabled={activePage >= items}
          >
            <span aria-label="Last">
              {last === true ? "\u00bb" : last}
            </span>
          </PaginationButton>
        )}
      </div>
    );
  }
}

Pagination.propTypes = propTypes;
Pagination.defaultProps = defaultProps;

export default bsClass("pagination", Pagination);
